'use client';

import BaseModal from '@c/modals/BaseModal';
import Button from '@ui/Button';
import { removeUndefinedRecursive } from '@util/firestore/products/products.service';
import { createLinearIssue } from '@util/firestore/support/support.service';
import { useToastContext } from 'context/ToastContext';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CircleDashedIcon,
  GhostIcon,
  HashIcon,
  Maximize2Icon,
  UserIcon,
  XIcon,
} from 'lucide-react';
import {
  LinearAssignee,
  LinearIssue,
  linearIssueSchema,
  LinearPriority,
  LinearStatus,
} from 'models/linear';
import { useEffect, useId, useState } from 'react';

const LINEAR_ASSIGNEE_OPTIONS = (
  Object.keys(LinearAssignee) as Array<keyof typeof LinearAssignee>
).map((name) => ({
  label: name,
  value: LinearAssignee[name],
}));

const LINEAR_STATUS_OPTIONS = (
  Object.keys(LinearStatus) as Array<keyof typeof LinearStatus>
).map((name) => ({
  label: name,
  value: LinearStatus[name],
}));

interface CreateLinearIssueModalProps {
  isOpen: boolean;
  chatId: string;
  dismiss: () => void;
}

const parseCommaSeparatedValues = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

/** Shared dark-field chrome (avoid @ui Input onDark, which stays white). */
const fieldGhost =
  'rounded-[10px] border border-white/[0.08] bg-[#1f1f1f] text-[1.4rem] leading-normal text-zinc-100 outline-none placeholder:text-zinc-500 focus:border-white/15 focus:ring-1 focus:ring-white/15';

export default function CreateLinearIssueModal({
  isOpen,
  chatId,
  dismiss,
}: CreateLinearIssueModalProps) {
  const fieldIdSuffix = useId();
  const { showErrorToast } = useToastContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<LinearPriority>(
    LinearPriority.NoPriority
  );
  const [assigneeId, setAssigneeId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [stateId, setStateId] = useState('');
  const [estimate, setEstimate] = useState('');
  const [labelIds, setLabelIds] = useState('');
  const [subscriberIds, setSubscriberIds] = useState('');
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setDescription('');
    setPriority(LinearPriority.NoPriority);
    setAssigneeId('');
    setProjectId('');
    setStateId('');
    setEstimate('');
    setLabelIds('');
    setSubscriberIds('');
    setDueDate('');
    setShowMoreFields(false);
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      showErrorToast('Issue title is required');
      return;
    }

    try {
      setIsSubmitting(true);
      const issue: LinearIssue = {
        chatId,
        title: title.trim(),
        description: description.trim() || undefined,
        priority: priority === LinearPriority.NoPriority ? undefined : priority,
        assigneeId: assigneeId.trim() || undefined,
        projectId: projectId.trim() || undefined,
        stateId: stateId.trim() || undefined,
        estimate: estimate ? Number(estimate) : undefined,
        labelIds: labelIds ? parseCommaSeparatedValues(labelIds) : undefined,
        subscriberIds: subscriberIds
          ? parseCommaSeparatedValues(subscriberIds)
          : undefined,
        dueDate: dueDate ? new Date(`${dueDate}T00:00:00`) : undefined,
      };
      const removedUndefined = removeUndefinedRecursive(issue);
      const parsedIssue = linearIssueSchema.parse(removedUndefined);
      const issueUrl = await createLinearIssue(parsedIssue);

      window.open(issueUrl, '_blank');

      dismiss();
    } catch (error) {
      showErrorToast(
        (error as Error).message || 'Failed to create Linear issue'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseModal isOpen={isOpen} dismiss={dismiss} hideCloseIcon darkPanel>
      <div className="flex w-[min(96vw,72rem)] flex-col px-6 pb-6 pt-4 sm:px-8 sm:pb-8 sm:pt-5">
        {/* Header — Linear-style */}
        <div className="mb-5 flex items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-2 text-[1.3rem] text-zinc-400">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-zinc-300"
              aria-hidden
            >
              <GhostIcon className="h-4 w-4" />
            </span>
            <span className="inline-flex items-center rounded-md bg-zinc-800 px-2 py-0.5 text-[1.2rem] font-medium tracking-wide text-zinc-200">
              MXS
            </span>
            <ChevronRightIcon className="h-4 w-4 shrink-0 text-zinc-600" />
            <span className="truncate font-medium text-zinc-200">
              New issue
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-white/5 hover:text-zinc-300"
              aria-label="Expand"
            >
              <Maximize2Icon className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-white/5 hover:text-zinc-300"
              aria-label="Close"
              onClick={dismiss}
            >
              <XIcon className="h-[1.8rem] w-[1.8rem]" />
            </button>
          </div>
        </div>

        {/* Title — borderless like Linear */}
        <label
          htmlFor={`linear-issue-title-${fieldIdSuffix}`}
          className="sr-only"
        >
          Issue title
        </label>
        <input
          id={`linear-issue-title-${fieldIdSuffix}`}
          type="text"
          placeholder="Issue title"
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full border-none bg-transparent text-[2.4rem] font-semibold tracking-tight text-zinc-100 outline-none placeholder:text-zinc-500 sm:text-[2.8rem]"
        />

        <label
          htmlFor={`linear-issue-desc-${fieldIdSuffix}`}
          className="sr-only"
        >
          Description
        </label>
        <textarea
          id={`linear-issue-desc-${fieldIdSuffix}`}
          placeholder="Add description..."
          rows={5}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={`mt-3 min-h-[10rem] w-full resize-none py-4 text-[1.5rem] ${fieldGhost}`}
        />

        {/* Property chips row */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="relative inline-flex h-10 min-w-[12.5rem] max-w-[18rem] items-center gap-1.5 rounded-full border border-white/[0.08] bg-[#252525] py-0 pl-3 pr-1 text-[1.3rem] text-zinc-400">
            <CircleDashedIcon className="pointer-events-none h-3.5 w-3.5 shrink-0 opacity-70" />
            <select
              aria-label="Status"
              value={stateId}
              onChange={(e) => setStateId(e.target.value)}
              className="h-full min-w-0 flex-1 cursor-pointer appearance-none border-none bg-transparent pr-7 text-[1.3rem] text-zinc-200 outline-none"
            >
              <option value="">Status</option>
              {LINEAR_STATUS_OPTIONS.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          </span>

          <span className="relative inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-full border border-white/[0.08] bg-[#252525] py-0 pl-3 pr-1 text-[1.3rem] text-zinc-400">
            <HashIcon className="pointer-events-none h-3.5 w-3.5 shrink-0 opacity-70" />
            <select
              aria-label="Priority"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
              className="h-full max-w-[9.6rem] cursor-pointer appearance-none border-none bg-transparent pr-7 text-[1.3rem] text-zinc-200 outline-none"
            >
              <option value={LinearPriority.NoPriority}>Priority</option>
              <option value={LinearPriority.Urgent}>Urgent</option>
              <option value={LinearPriority.High}>High</option>
              <option value={LinearPriority.Medium}>Medium</option>
              <option value={LinearPriority.Low}>Low</option>
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          </span>

          <span className="relative inline-flex h-10 min-w-[12rem] max-w-[16rem] items-center gap-1.5 rounded-full border border-white/[0.08] bg-[#252525] py-0 pl-3 pr-1 text-[1.3rem] text-zinc-400">
            <UserIcon className="pointer-events-none h-3.5 w-3.5 shrink-0 opacity-70" />
            <select
              aria-label="Assignee"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="h-full min-w-0 flex-1 cursor-pointer appearance-none border-none bg-transparent pr-7 text-[1.3rem] text-zinc-200 outline-none"
            >
              <option value="">Assignee</option>
              {LINEAR_ASSIGNEE_OPTIONS.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          </span>

          {/* <button
            type="button"
            onClick={() => setShowMoreFields((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/[0.08] bg-[#252525] text-zinc-500 transition-colors hover:border-white/15 hover:text-zinc-300"
            aria-expanded={showMoreFields}
            aria-label="More fields"
          >
            <MoreHorizontalIcon className="h-4 w-4" />
          </button> */}
        </div>

        {showMoreFields ? (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-[1.2rem] font-medium text-zinc-500">
              Project
              <input
                type="text"
                placeholder="Project ID"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className={`h-11 px-3 ${fieldGhost}`}
                aria-label="Project ID"
              />
            </label>
            <label className="flex flex-col gap-1.5 text-[1.2rem] font-medium text-zinc-500">
              Estimate
              <input
                type="number"
                placeholder="Estimate points"
                value={estimate}
                onChange={(e) => setEstimate(e.target.value)}
                className={`h-11 px-3 ${fieldGhost}`}
                aria-label="Estimate"
              />
            </label>
            <label className="flex flex-col gap-1.5 text-[1.2rem] font-medium text-zinc-500 sm:col-span-2">
              Labels
              <input
                type="text"
                placeholder="labelA, labelB"
                value={labelIds}
                onChange={(e) => setLabelIds(e.target.value)}
                className={`h-11 px-3 ${fieldGhost}`}
                aria-label="Label IDs"
              />
            </label>
            <label className="flex flex-col gap-1.5 text-[1.2rem] font-medium text-zinc-500">
              Due date
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className={`h-11 px-3 ${fieldGhost}`}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-[1.2rem] font-medium text-zinc-500 sm:col-span-2">
              Subscriber IDs
              <input
                type="text"
                placeholder="id1, id2"
                value={subscriberIds}
                onChange={(e) => setSubscriberIds(e.target.value)}
                className={`h-11 px-3 ${fieldGhost}`}
              />
            </label>
          </div>
        ) : null}

        {/* Footer */}
        <div className="mt-8 flex flex-wrap items-center justify-end gap-4 border-t border-white/[0.06] pt-5">
          {/* <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-white/5 hover:text-zinc-300"
            aria-label="Attachments"
          >
            <PaperclipIcon className="h-[1.8rem] w-[1.8rem]" />
          </button> */}
          {/* <div className="flex flex-wrap items-center justify-end gap-4 sm:gap-5"> */}
          <Button
            type="affirm"
            text="Create issue"
            width="mid"
            height="smaller"
            loading={isSubmitting}
            disabled={!title.trim()}
            onClick={handleSubmit}
            className="!mx-0 !rounded-[10px] !px-6 !font-semibold hover:!bg-[#3e3dcf]"
          />
          {/* </div> */}
        </div>
      </div>
    </BaseModal>
  );
}
